import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  ArrowLeft, Trophy, Target, TrendingUp, AlertTriangle, 
  CheckCircle, BarChart3, Sparkles, Headphones, BookOpen, PenTool, Mic
} from 'lucide-react';

const ProgressPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await api.get('/progress');
      setProgress(response.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
      toast.error('Failed to load progress');
    } finally {
      setLoading(false);
    }
  };

  const levelColors = {
    A2: { gradient: "from-green-400 to-green-600", bg: "bg-green-100", text: "text-green-700" },
    B1: { gradient: "from-blue-400 to-blue-600", bg: "bg-blue-100", text: "text-blue-700" },
    B2: { gradient: "from-purple-400 to-purple-600", bg: "bg-purple-100", text: "text-purple-700" }
  };

  const moduleIcons = {
    comprehension_orale: Headphones,
    comprehension_ecrite: BookOpen,
    production_ecrite: PenTool,
    production_orale: Mic
  };

  const moduleNames = {
    comprehension_orale: "Listening",
    comprehension_ecrite: "Reading",
    production_ecrite: "Writing",
    production_orale: "Speaking"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  const totalCompleted = Object.values(progress?.levels || {}).reduce((sum, l) => sum + l.completed_exercises, 0);
  const totalPossible = Object.values(progress?.levels || {}).reduce((sum, l) => sum + l.total_exercises, 0);

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="font-bold text-lg" style={{ fontFamily: 'Syne, sans-serif' }}>
            Your Progress
          </h1>
          <Link to="/levels">
            <Button variant="outline" className="rounded-full">All Levels</Button>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Overall Stats */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 border border-border/50"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-muted-foreground">Completed</span>
            </div>
            <div className="text-3xl font-bold">{totalCompleted}/{totalPossible}</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 border border-border/50"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-primary" />
              </div>
              <span className="text-sm text-muted-foreground">Overall</span>
            </div>
            <div className="text-3xl font-bold">{totalPossible > 0 ? Math.round((totalCompleted / totalPossible) * 100) : 0}%</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 border border-border/50"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <Target className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm text-muted-foreground">Active Level</span>
            </div>
            <div className="text-3xl font-bold">{progress?.selected_level || '-'}</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-6 border border-border/50"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-sm text-muted-foreground">Levels Active</span>
            </div>
            <div className="text-3xl font-bold">{Object.keys(progress?.levels || {}).filter(l => progress.levels[l].completed_exercises > 0).length}/3</div>
          </motion.div>
        </div>

        {/* Progress by Level */}
        <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>Progress by Level</h2>
        
        <div className="space-y-6">
          {['A2', 'B1', 'B2'].map((level, index) => {
            const levelData = progress?.levels?.[level] || { completed_exercises: 0, total_exercises: 40, progress_percent: 0, modules: {} };
            const colors = levelColors[level];
            
            return (
              <motion.div
                key={level}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="bg-white rounded-2xl border border-border/50 overflow-hidden"
              >
                <div className={`h-2 bg-gradient-to-r ${colors.gradient}`} />
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${colors.gradient} flex items-center justify-center text-white text-2xl font-bold`}>
                        {level}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold">DELF {level}</h3>
                        <p className="text-muted-foreground text-sm">
                          {levelData.completed_exercises} of {levelData.total_exercises} exercises
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold">{Math.round(levelData.progress_percent)}%</div>
                      <Progress value={levelData.progress_percent} className="w-32 h-2 mt-2" />
                    </div>
                  </div>

                  {/* Module breakdown */}
                  <div className="grid grid-cols-4 gap-4">
                    {Object.entries(moduleIcons).map(([moduleKey, Icon]) => {
                      const moduleCompleted = levelData.modules?.[moduleKey] || 0;
                      return (
                        <div key={moduleKey} className="text-center">
                          <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center mx-auto mb-2`}>
                            <Icon className={`w-6 h-6 ${colors.text}`} />
                          </div>
                          <div className="text-sm font-medium">{moduleCompleted}/10</div>
                          <div className="text-xs text-muted-foreground">{moduleNames[moduleKey]}</div>
                        </div>
                      );
                    })}
                  </div>

                  {levelData.completed_exercises > 0 && (
                    <div className="mt-4 pt-4 border-t flex justify-end">
                      <Button
                        variant="outline"
                        className="rounded-full"
                        onClick={() => navigate(`/level/${level}`)}
                      >
                        Continue {level}
                      </Button>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default ProgressPage;
